import React, { useEffect, useMemo, useState } from "react";
import { Heart, Search, X, Sparkles, Loader2, Send, Flower2 } from "lucide-react";
import { toast } from "sonner";
import api from "../store/api";
import DahliasShell from "../components/dahlias/DahliasShell";
import { CrushDrawer } from "../components/crush/CrushDrawer";
import "../components/crush/crush.css";

const CRUSH_KEY = "heritage-crush-list";
const loadCrush = () => { try { return JSON.parse(localStorage.getItem(CRUSH_KEY) || "{}"); } catch { return {}; } };

export default function CuttingCrushClub() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ colour: "", form: "", size: "" });
  const [crush, setCrush] = useState(loadCrush);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/crush/collection").then((r) => setData(r.data)).catch(() => toast.error("The collection is having a moment , try again shortly.")); }, []);
  useEffect(() => { localStorage.setItem(CRUSH_KEY, JSON.stringify(crush)); }, [crush]);

  const varieties = data?.varieties || [];
  const shown = useMemo(() => varieties.filter((v) => {
    const s = q.trim().toLowerCase();
    if (s && !`${v.name} ${v.description} ${v.colour} ${v.form}`.toLowerCase().includes(s)) return false;
    return (!filters.colour || v.colour === filters.colour) && (!filters.form || v.form === filters.form) && (!filters.size || v.size === filters.size);
  }), [varieties, q, filters]);

  const toggle = (v) => {
    setCrush((c) => {
      const next = { ...c };
      if (next[v.id]) { delete next[v.id]; toast(`${v.name} , un-crushed. It happens.`); }
      else { next[v.id] = { variety_id: v.id, name: v.name, image: v.image, qty: 1, note: "" }; toast.success(`♡ ${v.name} added to your Crush List`); }
      return next;
    });
  };
  const count = Object.keys(crush).length;

  return (
    <DahliasShell>
      <div className="crush-page" data-testid="crush-page">
        <section className="crush-hero wavy-bottom">
          <p className="crush-kicker">Heritage Dahlias · 2026/27 collection</p>
          <h1 className="crush-title">The Cutting Crush Club <span aria-hidden>♡</span></h1>
          <p className="crush-tagline">Fall in love. Shoot your shot. We're rooting for you.</p>
          <p className="crush-intro">
            Browse the whole Heritage collection, heart the dahlias that make your pulse race, then send us one combined cutting request.
            No store, no checkout , just you, a wishlist, and a nursery full of mother plants that might say yes.
          </p>
          <div className="crush-fineprint" data-testid="crush-disclaimer">
            A cutting request is an expression of interest, not a reservation. Availability depends on plant health, propagation success and
            the season , Heritage confirms availability and pricing before anything is ever charged.
          </div>
        </section>

        <section className="crush-toolbar">
          <label className="crush-search">
            <Search className="w-4 h-4" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name, colour or form…" data-testid="crush-search" />
            {q && <button type="button" onClick={() => setQ("")} aria-label="Clear"><X className="w-4 h-4" /></button>}
          </label>
          {[["colour", "Colour", data?.colours], ["form", "Flower form", data?.forms], ["size", "Bloom size", data?.sizes]].map(([k, label, opts]) => (
            <select key={k} value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} data-testid={`crush-filter-${k}`} className="crush-select">
              <option value="">{label}: any</option>
              {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <span className="crush-count" data-testid="crush-result-count">{shown.length} of {varieties.length} dahlias</span>
        </section>

        {!data ? (
          <div className="crush-loading"><Loader2 className="w-6 h-6 animate-spin" /> Setting the table…</div>
        ) : (
          <section className="crush-grid" data-testid="crush-grid">
            {shown.map((v, i) => <VarietyCard key={v.id} v={v} crushed={!!crush[v.id]} onToggle={() => toggle(v)} delay={i % 12} />)}
            {shown.length === 0 && <p className="crush-empty">No dahlia matches that , try a broader search. Love is out there.</p>}
          </section>
        )}

        <button type="button" className={`crush-fab ${count ? "has-items" : ""}`} onClick={() => setOpen(true)} data-testid="crush-list-open">
          <Heart className="w-5 h-5" fill={count ? "currentColor" : "none"} />
          <span>My Crush List</span>
          {count > 0 && <span className="crush-badge" data-testid="crush-list-count">{count}</span>}
        </button>

        {open && (
          <CrushDrawer crush={crush} setCrush={setCrush} onClose={() => setOpen(false)} onDone={(res) => { setDone(res); setCrush({}); setOpen(false); }} />
        )}
        {done && <MatchCard res={done} onClose={() => setDone(null)} />}
      </div>
    </DahliasShell>
  );
}

function VarietyCard({ v, crushed, onToggle, delay }) {
  const closed = v.request_status === "closed";
  return (
    <article className={`crush-card ${crushed ? "crushed" : ""}`} style={{ animationDelay: `${delay * 45}ms` }} data-testid={`crush-card-${v.id}`}>
      <div className="crush-photo">
        <img src={v.image} alt={v.name} loading="lazy" />
        {v.popular && <span className="crush-pill">Everyone's crush</span>}
        <button type="button" className={`crush-heart ${crushed ? "on" : ""}`} onClick={onToggle} disabled={closed} aria-label={crushed ? "Remove from crush list" : "Add to crush list"} data-testid={`crush-heart-${v.id}`}>
          <Heart className="w-5 h-5" fill={crushed ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="crush-body">
        <h3>{v.name}</h3>
        <p className="crush-meta">{[v.colour, v.form, v.size].filter(Boolean).join(" · ") || "Heritage named variety"}</p>
        <p className="crush-desc">{v.description}</p>
        <button type="button" className={`crush-request ${crushed ? "on" : ""}`} onClick={onToggle} disabled={closed} data-testid={`crush-request-${v.id}`}>
          {closed ? <><Flower2 className="w-4 h-4" /> Taking a season off</> : crushed ? <><Heart className="w-4 h-4" fill="currentColor" /> On your list</> : <><Heart className="w-4 h-4" /> Request cuttings</>}
        </button>
      </div>
    </article>
  );
}

function MatchCard({ res, onClose }) {
  return (
    <div className="crush-overlay" onClick={onClose}>
      <div className="crush-match" onClick={(e) => e.stopPropagation()} data-testid="crush-success">
        <Sparkles className="w-8 h-8" />
        <p className="crush-kicker">Shot fired</p>
        <h2>{res.label}</h2>
        <p>Your reference is <strong data-testid="crush-success-ref">{res.ref}</strong>. {res.blurb}</p>
        <p className="crush-small">
          {res.in_hq ? "Follow every update under My Crushes in Heritage HQ." : "Create or log in to your Heritage HQ account with the same email to follow your crush , we've emailed you a copy too."}
        </p>
        <div className="crush-actions">
          <a href="/account/crushes" className="crush-btn" data-testid="crush-success-hq"><Send className="w-4 h-4" /> Open Heritage HQ</a>
          <button type="button" onClick={onClose} className="crush-btn ghost">Keep browsing</button>
        </div>
      </div>
    </div>
  );
}
